/**
 * AdminSurveys — CMS page for reviewing pending student surveys and
 * approving / rejecting them. Approved responses feed the aggregated
 * StudentVoiceTab on public uni + course pages.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const STATUS_TABS = [
  ["pending",  "Pending"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["all",      "All"],
];

export default function AdminSurveys() {
  const [status, setStatus] = useState("pending");
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api.get(`/admin/surveys?status=${status}&limit=200`)
      .then((r) => setSurveys(r.data.surveys || []))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load surveys."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const act = async (id, action, reason = null) => {
    try {
      await api.post(`/admin/surveys/${id}/${action}`, action === "reject" ? { reason } : {});
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || `Failed to ${action}.`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-surveys-page">
      <div className="mb-6">
        <div className="label-uppercase">Admin · CMS</div>
        <h1 className="font-editorial text-4xl text-[#002147]">Student Surveys</h1>
        <p className="mt-2 text-sm text-stone-600 max-w-3xl">
          Every response lands in <strong>pending</strong>. Approve to include it in the
          aggregated ratings displayed on the public uni / course pages;
          reject if it's spam, PII-leaking, or clearly abusive. Aggregate
          only surfaces on a page once ≥5 approved responses exist for
          the (university, course) target.
        </p>
      </div>

      <div className="flex gap-2 mb-6" data-testid="admin-surveys-status-tabs">
        {STATUS_TABS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)}
                  data-testid={`admin-surveys-status-${v}`}
                  className={
                    "px-3 py-1.5 rounded-full text-sm border transition-colors " +
                    (status === v
                      ? "bg-[#002147] text-white border-[#002147]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-[#002147]")
                  }>
            {l}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-stone-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {!loading && surveys.length === 0 && (
        <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-6 text-sm text-stone-600">
          No {status} surveys.
        </div>
      )}
      <div className="space-y-3">
        {surveys.map((s) => (
          <SurveyRow key={s.id} s={s} expanded={expanded === s.id}
                     onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                     onApprove={() => act(s.id, "approve")}
                     onReject={() => {
                       const r = window.prompt("Reason for rejection (optional):", "");
                       if (r !== null) act(s.id, "reject", r);
                     }} />
        ))}
      </div>
    </div>
  );
}


function SurveyRow({ s, expanded, onToggle, onApprove, onReject }) {
  const ratingKeys = Object.keys(s).filter(
    (k) => /^(overall_|teaching_|value_|campus_|student_|career_|content_|assessment_|workload_|social_|accommodation)/.test(k)
       && typeof s[k] === "number"
  );
  return (
    <div className="rounded-md border border-stone-200 bg-white" data-testid={`survey-row-${s.id}`}>
      <div className="flex flex-wrap items-baseline gap-3 px-4 py-3 cursor-pointer"
           onClick={onToggle}>
        <span className={
          "px-2 py-0.5 rounded-full text-[10px] font-underground-sc tracking-wider " +
          (s.status === "pending"  ? "bg-amber-100 text-amber-800" :
           s.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                                      "bg-stone-100 text-stone-700")
        }>
          {s.status}
        </span>
        <span className="text-sm text-stone-800">
          {s.degree_id ? "Course" : "Uni-wide"} · {s.university_id}{s.degree_id ? ` · ${s.degree_id}` : ""}
        </span>
        <span className="text-[12px] font-mono text-stone-500">
          {s.student_email_masked} · {s.year_of_study} · {s.course_name}
        </span>
        <span className="ml-auto text-[10px] font-mono text-stone-400">
          {(s.submitted_at || "").slice(0, 10)}
        </span>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-stone-100 space-y-3">
          <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MetaField label="Student name" value={s.student_name} />
            <MetaField label="Email (full)" value={s.student_email} />
            <MetaField label="Would recommend" value={s.would_recommend || "—"} />
            <MetaField label="Follow-up OK" value={s.follow_up_ok ? "yes" : "no"} />
          </div>
          {ratingKeys.length > 0 && (
            <div>
              <div className="label-uppercase mb-1 text-[10px]">Ratings</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[12px] font-mono">
                {ratingKeys.map((k) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-stone-600">{k}</span>
                    <span className="font-semibold text-[#002147]">{s[k]}/5</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {s.most_positive && <QuoteBlock label="Loved" text={s.most_positive} tint="emerald" />}
          {s.most_negative && <QuoteBlock label="Disliked" text={s.most_negative} tint="red" />}
          {s.advice_to_applicants && <QuoteBlock label="Advice" text={s.advice_to_applicants} tint="stone" />}

          {s.status === "pending" && (
            <div className="flex gap-2 pt-2">
              <button onClick={onApprove}
                      data-testid={`admin-approve-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Approve
              </button>
              <button onClick={onReject}
                      data-testid={`admin-reject-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700">
                <XCircle className="h-4 w-4" /> Reject
              </button>
            </div>
          )}
          {s.status !== "pending" && (
            <div className="pt-1 text-[11px] font-mono text-stone-500">
              {s.status} by {s.reviewed_by || "—"} · {s.reviewed_at || "—"}
              {s.reject_reason && <> · reason: {s.reject_reason}</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">{label}</div>
      <div className="text-[13px] text-stone-800">{value || "—"}</div>
    </div>
  );
}
function QuoteBlock({ label, text, tint }) {
  const bg = tint === "emerald" ? "bg-emerald-50 border-emerald-200" :
             tint === "red"     ? "bg-red-50 border-red-200" :
                                  "bg-stone-50 border-stone-200";
  return (
    <div className={`rounded border ${bg} p-2.5`}>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">{label}</div>
      <p className="text-[13px] text-stone-800 leading-snug">{text}</p>
    </div>
  );
}
